import { useState, useEffect, useCallback, useRef } from 'react';
import { syncService, SyncStatus } from './syncService';
import { 
    markEventAsCached, 
    isEventCached, 
    getCachedEvents,
    getPendingSyncItemsByEvent,
    removeCachedEvent,
    CachedEvent 
} from './syncQueue';
import { offlineStoreDb } from './offlineStoreDb';
import agent from '../api/agent';
import { upsertOfflineDbTableEntry, OfflineStoreKeys, offlineJoin } from './offline.util';
import { pendingStatus } from '../models/staticFields';
import { EventModel } from '../models/events/events';

export function useSyncStatus() {
    const [status, setStatus] = useState<SyncStatus>({
        isSyncing: false,
        pendingCount: 0,
        lastSyncAt: null,
        error: null
    });

    useEffect(() => {
        const unsubscribe = syncService.subscribe(setStatus);
        return unsubscribe;
    }, []);

    const syncNow = useCallback(() => {
        syncService.syncNow();
    }, []);

    const retryFailed = useCallback(() => {
        syncService.retryFailed();
    }, []);

    return { ...status, syncNow, retryFailed };
}

export function useOnlineStatus() {
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return isOnline;
}

export function usePreloadEventData() {
    const [isPreloading, setIsPreloading] = useState(false);
    const [progress, setProgress] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const preloadEvent = useCallback(async (eventId: string, eventName?: string): Promise<boolean> => {
        if (!navigator.onLine) {
            console.log('[usePreloadEventData] Cannot preload while offline');
            return false;
        }

        setIsPreloading(true);
        setError(null);
        setProgress('Starting...');

        try {
            setProgress('Caching event details...');
            const event = await agent.Events.getById(eventId);
            await upsertOfflineDbTableEntry(event, OfflineStoreKeys.EVENTS);

            setProgress('Caching stands...');
            const stands = await agent.Stands.getAllByEventId(eventId);
            for (const stand of stands) {
                await upsertOfflineDbTableEntry(stand, OfflineStoreKeys.STANDS);
            }

            setProgress('Caching stand sectors...');
            const sectors = await agent.StandSectors.getAll(eventId);
            for (const sector of sectors) {
                await upsertOfflineDbTableEntry(sector, OfflineStoreKeys.STAND_SECTORS);
            }

            setProgress('Caching orders...');
            const orders = await agent.Orders.getAllByEventId(eventId);
            for (const order of orders) {
                await upsertOfflineDbTableEntry(order, OfflineStoreKeys.ORDERS);
            }

            setProgress('Caching order items...');
            const orderItems = await agent.OrderItems.getAllByEventId(eventId);
            for (const orderItem of orderItems) {
                await upsertOfflineDbTableEntry(orderItem, OfflineStoreKeys.ORDER_ITEMS);
            }

            setProgress('Caching customers...');
            const customers = await agent.Customers.getByEvent(eventId);
            await offlineJoin('eventId', eventId, 'customerId', customers, offlineStoreDb.eventCustomers);
            for (const customer of customers) {
                await upsertOfflineDbTableEntry(customer, OfflineStoreKeys.CUSTOMERS);
            }

            setProgress('Caching products...');
            const products = await agent.Products.getByEvent(eventId);
            await offlineJoin('eventId', eventId, 'productId', products, offlineStoreDb.eventProducts);
            for (const product of products) {
                await upsertOfflineDbTableEntry(product, OfflineStoreKeys.PRODUCTS);
            }

            setProgress('Caching master data...');
            const productTypes = await agent.ProductTypes.getAll();
            for (const pt of productTypes) {
                await upsertOfflineDbTableEntry(pt, OfflineStoreKeys.PRODUCT_TYPES);
            }

            const standTypes = await agent.StandTypes.getAll();
            for (const st of standTypes) {
                await upsertOfflineDbTableEntry(st, OfflineStoreKeys.STAND_TYPES);
            }

            const countries = await agent.Countries.getAll();
            for (const country of countries) {
                await upsertOfflineDbTableEntry(country, OfflineStoreKeys.COUNTRIES);
            }

            await markEventAsCached(eventId, eventName || event.name || eventId, true);

            setProgress('Complete!');
            console.log(`[usePreloadEventData] Successfully cached all data for event ${eventId}`);
            return true;

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to preload event data';
            console.error('[usePreloadEventData] Error:', err);
            setError(errorMessage);
            return false;
        } finally {
            setIsPreloading(false);
        }
    }, []);

    return { preloadEvent, isPreloading, progress, error };
}

export function useCachedEvents() {
    const [cachedEvents, setCachedEvents] = useState<CachedEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const refresh = useCallback(async () => {
        setIsLoading(true);
        try {
            const events = await getCachedEvents();
            setCachedEvents(events);
        } catch (error) {
            console.error('[useCachedEvents] Error fetching cached events:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { cachedEvents, isLoading, refresh };
}

export function useIsEventCached(eventId: string) {
    const [isCached, setIsCached] = useState(false);
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
        const checkCache = async () => {
            setIsChecking(true);
            try {
                const cached = await isEventCached(eventId);
                setIsCached(cached);
            } catch (error) {
                console.error('[useIsEventCached] Error:', error);
                setIsCached(false);
            } finally {
                setIsChecking(false);
            }
        };

        checkCache();
    }, [eventId]);

    return { isCached, isChecking };
}

export function useEventPendingSyncCount(eventId: string) {
    const [pendingCount, setPendingCount] = useState(0);

    useEffect(() => {
        const checkPending = async () => {
            try {
                const items = await getPendingSyncItemsByEvent(eventId);
                setPendingCount(items.length);
            } catch (error) {
                console.error('[useEventPendingSyncCount] Error:', error);
            }
        };

        checkPending();

        const unsubscribe = syncService.subscribe(() => {
            checkPending();
        });

        return unsubscribe;
    }, [eventId]);

    return pendingCount;
}

export function useCachedEventIds() {
    const [cachedIds, setCachedIds] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(true);

    const refresh = useCallback(async () => {
        setIsLoading(true);
        try {
            const events = await getCachedEvents();
            setCachedIds(new Set(events.filter(e => e.isFullyCached).map(e => e.id)));
        } catch (error) {
            console.error('[useCachedEventIds] Error:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { cachedIds, isLoading, refresh };
}

/**
 * Clears all cached data from IndexedDB for a specific event.
 * Removes event-scoped entities (stands, orders, orderItems, eventCustomers,
 * eventProducts, standSectors) and the event itself, plus the cachedEvents entry.
 */
async function clearEventCache(eventId: string): Promise<void> {
    try {
        await offlineStoreDb.stands.where('eventId').equals(eventId).delete();
        await offlineStoreDb.orders.where('eventId').equals(eventId).delete();
        await offlineStoreDb.orderItems.where('eventId').equals(eventId).delete();
        await offlineStoreDb.eventCustomers.where('eventId').equals(eventId).delete();
        await offlineStoreDb.eventProducts.where('eventId').equals(eventId).delete();
        await offlineStoreDb.standSectors.where('eventId').equals(eventId).delete();
        await offlineStoreDb.events.delete(eventId);
        await removeCachedEvent(eventId);
        console.log(`[AutoCache] Cleared cache for past event ${eventId}`);
    } catch (error) {
        console.error(`[AutoCache] Error clearing cache for event ${eventId}:`, error);
    }
}

const AUTO_CACHE_INTERVAL_MS = 2 * 60 * 60 * 1000; // 2 hours
const LAST_CHECK_KEY = 'cupcycle_auto_cache_last_check';

/**
 * Hook that automatically:
 * 1. Caches all active/upcoming events (endDate >= today) for offline use.
 * 2. Clears cached data for past/finished events (endDate < today).
 * Runs on mount, and then every 2 hours.
 */
export function useAutoEventCacheManager() {
    const { preloadEvent } = usePreloadEventData();
    const isOnline = useOnlineStatus();
    const runningRef = useRef(false);

    const runCacheManager = useCallback(async () => {
        if (!navigator.onLine || runningRef.current) return;
        runningRef.current = true;

        try {
            const now = new Date();
            const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            // Fetch all events from the API
            let allEvents: EventModel[] = [];
            try {
                allEvents = await agent.Events.getAll({});
            } catch (err) {
                console.error('[AutoCache] Failed to fetch events:', err);
                return;
            }

            // Get currently cached event IDs
            const cachedEvents = await getCachedEvents();
            const cachedIds = new Set(cachedEvents.filter(e => e.isFullyCached).map(e => e.id));

            // Auto-cache active/upcoming events (endDate >= today)
            for (const event of allEvents) {
                if (!event.endDate) continue;
                const endDate = new Date(event.endDate);
                if (endDate >= todayStart && !cachedIds.has(event.id)) {
                    console.log(`[AutoCache] Auto-caching event: ${event.name} (${event.id})`);
                    try {
                        await preloadEvent(event.id, event.name);
                    } catch (err) {
                        console.error(`[AutoCache] Failed to cache event ${event.id}:`, err);
                    }
                }
            }

            // Clear past events from cache (endDate < today)
            const allEventIds = new Set(allEvents.map(e => e.id));
            for (const cached of cachedEvents) {
                const event = allEvents.find(e => e.id === cached.id);
                if (event && event.endDate) {
                    const endDate = new Date(event.endDate);
                    if (endDate < todayStart) {
                        console.log(`[AutoCache] Clearing past event from cache: ${cached.name} (${cached.id})`);
                        await clearEventCache(cached.id);
                    }
                } else if (!allEventIds.has(cached.id)) {
                    // Event was deleted from backend — clean up cache
                    console.log(`[AutoCache] Clearing deleted event from cache: ${cached.name} (${cached.id})`);
                    await clearEventCache(cached.id);
                }
            }

            localStorage.setItem(LAST_CHECK_KEY, now.toISOString());
        } catch (error) {
            console.error('[AutoCache] Error in cache manager:', error);
        } finally {
            runningRef.current = false;
        }
    }, [preloadEvent]);

    useEffect(() => {
        if (!isOnline) return;

        // Check if enough time has passed since the last run
        const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
        const shouldRunNow = !lastCheck || (Date.now() - new Date(lastCheck).getTime()) > AUTO_CACHE_INTERVAL_MS;

        if (shouldRunNow) {
            runCacheManager();
        }

        // Set up 2-hour interval
        const interval = setInterval(runCacheManager, AUTO_CACHE_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [isOnline, runCacheManager]);

    return { runCacheManager };
}
